import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { useDragToScroll } from "../../hooks/useDragToScroll";
import { KanbanTopScrollbar } from "../../components/KanbanTopScrollbar";
import { ArrowLeft, Plus, Calendar, AlertTriangle, Trash2, Settings } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { buscarProjeto } from "../../services/projetos";
import { listarTarefas, Tarefa, atualizarTarefa, criarTarefa, deletarTarefa } from "../../services/tarefas";
import { TarefaDrawer } from "../tarefas/TarefaDrawer";
import { confirmarDuasVezes } from "../../utils/confirmarDuasVezes";
import { avisoToast } from "../../utils/avisoToast";
import { tarefaPodeVencer } from "../../utils/tarefaVencida";
import { listarColunasProjeto, mesclarColunasComBase, ColunaCustom } from "../../services/projetoColunas";
import { listarColunasKanban } from "../../services/colunasKanban";
import { registrarAjusteComAnexos } from "../../services/ajustes";
import { RegistrarAjusteModal, DadosAjuste } from "../../components/RegistrarAjusteModal";
import { ColunasKanbanModal } from "./ColunasKanbanModal";

export function KanbanBoard() {
  const { id: projetoId } = useParams<{ id: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [novaTarefa, setNovaTarefa] = useState({ titulo: "", atribuidoA: "", prazo: "", prioridade: "media" });
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [showConfigColunas, setShowConfigColunas] = useState(false);
  const podeEditarColunas = can(["gestor", "admin"]);

  const { data: colunasCustom = [] } = useQuery({
    queryKey: ["projeto-colunas", projetoId],
    queryFn: () => listarColunasProjeto(projetoId!),
    enabled: !!projetoId,
  });
  const { data: colunasGlobais = [] } = useQuery({
    queryKey: ["colunas-kanban", "ativas"],
    queryFn: () => listarColunasKanban(),
  });
  const base: ColunaCustom[] = colunasGlobais.map((c) => ({ status: c.chave, label: c.label, cor: c.cor, emoji: c.emoji }));
  const columns = mesclarColunasComBase(base, colunasCustom);

  const { data: projeto } = useQuery({
    queryKey: ["projeto", projetoId],
    queryFn: () => buscarProjeto(projetoId!),
    enabled: !!projetoId,
  });

  const { data: tarefas } = useQuery({
    queryKey: ["tarefas", { projetoId }],
    queryFn: () => listarTarefas({ projetoId }),
    enabled: !!projetoId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tarefa> }) => atualizarTarefa(id, data),
    onSuccess: (serverTask: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ["tarefas", { projetoId }] });
      queryClient.invalidateQueries({ queryKey: ["projeto", projetoId] });
      if (variables?.data?.status === "concluido" && serverTask?.status === "aguardando_aprovacao") {
        avisoToast("Enviado para Aprovação interna", `"${serverTask.titulo}" precisa da aprovação de um gestor/admin para ser finalizada.`, serverTask.id);
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: criarTarefa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tarefas", { projetoId }] });
      setShowForm(false);
      setNovaTarefa({ titulo: "", atribuidoA: "", prazo: "", prioridade: "media" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletarTarefa,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tarefas", { projetoId }] }),
  });

  const [ajustePendente, setAjustePendente] = useState<{ id: string; data: Partial<Tarefa> } | null>(null);
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);

  // Rolagem lateral do quadro: barra no topo + arrastar o fundo (grab-to-pan).
  // Sem auto-scroll no drag AQUI: neste quadro o container que rola é um div
  // simples (não o Droppable), então rolar durante o drag faria o card cair na
  // coluna errada — ver review jul/2026. O auto-scroll fica só no quadro de tarefas.
  const boardRef = useRef<HTMLDivElement>(null);
  const pan = useDragToScroll(boardRef);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId.replace(/-/g, "_");
    const tarefa = tarefas?.find((t) => t.id === draggableId);
    if (newStatus === "ajustes" && tarefa && tarefa.status !== "ajustes") {
      setAjustePendente({ id: draggableId, data: { status: newStatus } });
      return;
    }
    updateMutation.mutate({ id: draggableId, data: { status: newStatus } });
  };

  async function confirmarAjuste(d: DadosAjuste) {
    if (!ajustePendente) return;
    setSalvandoAjuste(true);
    try {
      // Move PRIMEIRO; só registra o ajuste se o move deu certo — evita "ajuste
      // fantasma" (registrado num card que ficou fora da coluna Ajustes).
      await updateMutation.mutateAsync(ajustePendente);
      const { arquivos, ...dadosAjuste } = d;
      const { falhas } = await registrarAjusteComAnexos(ajustePendente.id, dadosAjuste, arquivos);
      setAjustePendente(null); // fecha só no sucesso
      if (falhas.length) alert(`Ajuste registrado, mas ${falhas.length} anexo(s) não subiram: ${falhas.join(", ")}. Reabra o card e use "+ arquivo".`);
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || "Não foi possível mover o card / registrar o ajuste. Tente de novo.");
    } finally {
      setSalvandoAjuste(false);
    }
  }

  const handleCriarTarefa = () => {
    if (!novaTarefa.titulo || !novaTarefa.atribuidoA || !novaTarefa.prazo || !projetoId) return;
    createMutation.mutate({
      projetoId,
      titulo: novaTarefa.titulo,
      atribuidoA: novaTarefa.atribuidoA,
      prazo: novaTarefa.prazo,
      prioridade: novaTarefa.prioridade,
    });
  };

  const isVencida = (prazo: string, status: string) =>
    tarefaPodeVencer(status) && new Date(prazo) < new Date();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/projetos/${projetoId}`} className="p-2 rounded-lg hover:bg-white transition-colors">
          <ArrowLeft size={20} className="text-slate-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-800">
            {projeto?.nome} — Tarefas
          </h1>
          <p className="text-sm text-slate-500">{tarefas?.length || 0} tarefas</p>
        </div>
        {podeEditarColunas && (
          <button
            onClick={() => setShowConfigColunas(true)}
            className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            title="Personalizar colunas deste kanban"
          >
            <Settings size={16} /> Colunas
          </button>
        )}
        {can(["gestor", "admin"]) && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Plus size={18} /> Nova Tarefa
          </button>
        )}
      </div>

      {/* Nova tarefa form */}
      {showForm && can(["gestor", "admin"]) && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-medium text-slate-700 mb-3">Nova Tarefa</h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Título"
              value={novaTarefa.titulo}
              onChange={(e) => setNovaTarefa({ ...novaTarefa, titulo: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
            <input
              type="text"
              placeholder="ID do responsável"
              value={novaTarefa.atribuidoA}
              onChange={(e) => setNovaTarefa({ ...novaTarefa, atribuidoA: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
            <input
              type="date"
              value={novaTarefa.prazo}
              onChange={(e) => setNovaTarefa({ ...novaTarefa, prazo: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
            <select
              value={novaTarefa.prioridade}
              onChange={(e) => setNovaTarefa({ ...novaTarefa, prioridade: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            >
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleCriarTarefa}
              disabled={createMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {createMutation.isPending ? "Criando..." : "Criar"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Kanban columns */}
      <KanbanTopScrollbar targetRef={boardRef} />
      <DragDropContext onDragEnd={onDragEnd}>
        <div ref={boardRef} onMouseDown={pan.onMouseDown} className="flex gap-4 overflow-x-auto kanban-x-hidden">
          {columns.map((col) => (
            <div
              key={col.status}
              className="rounded-xl border border-slate-200 border-t-4 bg-white w-[270px] shrink-0"
              style={{ borderTopColor: col.cor }}
            >
              <div className="p-3 rounded-t-xl bg-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-slate-700 inline-flex items-center gap-1 truncate">
                    {col.emoji && <span>{col.emoji}</span>}
                    <span className="truncate">{col.label}</span>
                  </h3>
                  <span className="text-sm text-slate-500 shrink-0">
                    {tarefas?.filter((t) => t.status === col.status).length || 0}
                  </span>
                </div>
              </div>
              <Droppable droppableId={col.status}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    // overscroll-y-contain: no eixo X a cadeia tem que ficar livre pro
                    // swipe do dedo em cima dos cards rolar o quadro (ver TarefasKanban).
                    className="kanban-scroll p-3 space-y-3 min-h-[200px] max-h-[calc(100vh-260px)] overflow-y-auto overscroll-y-contain"
                  >
                    {tarefas
                      ?.filter((t) => t.status === col.status)
                      .map((tarefa, index) => (
                        <Draggable key={tarefa.id} draggableId={tarefa.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setDrawerId(tarefa.id)}
                              className={`bg-white rounded-lg border p-3 shadow-sm transition-shadow cursor-pointer hover:shadow-md ${
                                snapshot.isDragging ? "shadow-xl rotate-2" : ""
                              } ${tarefa.prioridade === "urgente" ? "border-l-4 border-l-red-500" : tarefa.prioridade === "alta" ? "border-l-4 border-l-orange-500" : tarefa.prioridade === "media" ? "border-l-4 border-l-blue-500" : ""}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <p className="font-medium text-sm text-slate-800">{tarefa.titulo}</p>
                                {can(["admin"]) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const ok = confirmarDuasVezes({
                                        primeiraMensagem: `Excluir definitivamente a tarefa "${tarefa.titulo}"?\n\nFeedbacks, anexos, subitens e comentários serão apagados.`,
                                        segundaMensagem: `CONFIRMAR exclusão da tarefa "${tarefa.titulo}"?\n\nEsta é a confirmação FINAL — não dá pra desfazer.`,
                                      });
                                      if (ok) deleteMutation.mutate(tarefa.id);
                                    }}
                                    className="text-slate-300 hover:text-red-500 transition-colors"
                                    title="Excluir tarefa definitivamente"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  {new Date(tarefa.prazo).toLocaleDateString("pt-BR")}
                                </span>
                                {isVencida(tarefa.prazo, tarefa.status) && (
                                  <span className="flex items-center gap-0.5 text-red-600 font-medium">
                                    <AlertTriangle size={12} /> Vencida
                                  </span>
                                )}
                              </div>
                              {tarefa.atribuido && (
                                <p className="text-xs text-slate-400 mt-1">{tarefa.atribuido.nome}</p>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {ajustePendente && (
        <RegistrarAjusteModal onConfirmar={confirmarAjuste} onCancelar={() => setAjustePendente(null)} salvando={salvandoAjuste} />
      )}
      {drawerId && <TarefaDrawer tarefaId={drawerId} onDuplicada={(novaId) => setDrawerId(novaId)} onClose={() => setDrawerId(null)} />}
      {showConfigColunas && projetoId && (
        <ColunasKanbanModal
          projetoId={projetoId}
          customsAtuais={colunasCustom}
          onClose={() => setShowConfigColunas(false)}
        />
      )}
    </div>
  );
}
